import "react-phone-input-2/lib/bootstrap.css";

import { useMutation } from "@apollo/client";
import { Button, Card, Checkbox, Col, DatePicker, Form, Input, Row, Select } from "antd";
import dayjs from "dayjs";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import PhoneInput from "react-phone-input-2";
import countryList from "react-select-country-list";

import useAuth from "../../../auth";
import { PARTICIPANT_PROFILE_UPDATE } from "../../../graphql/mutations";

import { isKuwaitNumberValid } from "helpers/common";

const { Option } = Select;

const ParticipantInfoCard = ({
  profileInfo,
  sociallyAuthenticatedUserEmail,
  userID,
  participantProfileDataRefetch,
}) => {
  const { t } = useTranslation();
  const { signIn, hasUsedSocialAuth } = useAuth();

  const hasCompletedProfile = !!profileInfo;

  const [submitting, setSubmitting] = useState(false);

  const [mobileNumber, setMobileNumber] = useState(null);
  const [guardianMobileNumber, setGuardianMobileNumber] = useState(null);
  const [emergencyContactNumber, setEmergencyContactNumber] = useState(null);

  const [languages, setLanguages] = useState([]);
  const [chronicDiseasesYesNo, setChronicDiseasesYesNoField] = useState(null);

  const [dateOfBirth, setDateOfBirth] = useState(null);

  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const errorMessageRef = useRef(null);
  const successMessageRef = useRef(null);

  const [updateProfileMutation] = useMutation(PARTICIPANT_PROFILE_UPDATE, {
    onCompleted: (data) => {
      if (data.participantProfileUpdate.success) {
        if (data.participantProfileUpdate.token)
          signIn({ token: data.participantProfileUpdate.token });
        setErrorMessage(null);
        setSuccessMessage(t("auth.participantProfile.updatedSuccessfullyMessage"));
        scrollToSuccess();
        setTimeout(() => {
          setSuccessMessage(null);
        }, 5000); // Clear success message after 5 seconds
        participantProfileDataRefetch();
      } else {
        setErrorMessage(data.participantProfileUpdate.errors[0]);
        setSuccessMessage(null);
        scrollToError();
      }
      setSubmitting(false);
    },
  });

  const onFinish = (values) => {
    setSubmitting(true);
    setErrorMessage(null);

    if (
      (mobileNumber && !isKuwaitNumberValid(mobileNumber)) ||
      (guardianMobileNumber && !isKuwaitNumberValid(guardianMobileNumber)) ||
      (emergencyContactNumber && !isKuwaitNumberValid(emergencyContactNumber))
    ) {
      setErrorMessage("Please enter a valid phone number (8 digits).");
      scrollToError();
      setSubmitting(false);
      return;
    }

    const inputData = {
      ...values,
    };

    const email = inputData["email"];
    delete inputData["email"];

    if (dateOfBirth) inputData["dateOfBirth"] = dateOfBirth;
    else delete inputData["dateOfBirth"];

    if (mobileNumber) inputData["mobileNumber"] = mobileNumber;
    else delete inputData["mobileNumber"];

    if (guardianMobileNumber) inputData["guardianMobileNumber"] = guardianMobileNumber;
    else delete inputData["guardianMobileNumber"];

    if (emergencyContactNumber) inputData["emergencyContactNumber"] = emergencyContactNumber;
    else delete inputData["emergencyContactNumber"];

    updateProfileMutation({
      variables: {
        userId: parseInt(userID),
        email,
        inputData: { ...inputData },
      },
    });
  };

  const onFinishFailed = (errorInfo) => {
    // eslint-disable-next-line no-console
    console.log("Failed:", errorInfo);
  };

  const scrollToError = () => {
    if (errorMessageRef.current) {
      errorMessageRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const scrollToSuccess = () => {
    if (successMessageRef.current) {
      successMessageRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const countriesList = countryList()
    .setLabel("PS", "Palestine")
    .getData()
    .filter((obj) => obj.label !== "Israel")
    .map((obj) => (
      <Option key={obj.value} value={obj.label}>
        {obj.label}
      </Option>
    ));

  return (
    <Card style={{ width: "100%" }}>
      <Form
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        scrollToFirstError={true}
        initialValues={
          hasCompletedProfile
            ? {
                ...profileInfo,
                dateOfBirth: dayjs(profileInfo.dateOfBirth, "YYYY-MM-DD"),
              }
            : {
                email: sociallyAuthenticatedUserEmail,
                fullNameEn: "",
                fullNameAr: "",
                civilIdNumber: "",
                dateOfBirth: null,
                mobileNumber: "",
                gender: "",
                guardianMobileNumber: "",
                guardianRelation: "",
                emergencyContactNumber: "",
                emergencyContactRelation: "",
                languages: [],
                chronicDiseasesYesNo: "",
                chronicDiseases: "",
                nationality: "",
                motherNationality: "",
                countryOfResidence: "",
              }
        }
      >
        <div ref={errorMessageRef}></div> {/* Error message ref */}
        {errorMessage && <p className="text-danger english-field">{errorMessage}</p>}
        <div ref={successMessageRef}></div> {/* Success message ref */}
        {successMessage && <p className="text-success">{successMessage}</p>}
        <Row gutter={[24, 0]}>
          <Col xs={24} lg={16}>
            <Form.Item
              label={t("auth.signUp.emailField")}
              name="email"
              help={t("auth.signUp.emailNote")}
              rules={[
                {
                  required: true,
                  message: t("auth.signUp.requiredFieldMessage"),
                },
                {
                  type: "email",
                  message: t("auth.signUp.validEmailAddressMessage"),
                },
              ]}
            >
              <Input className="english-field" disabled={hasUsedSocialAuth} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[24, 0]}>
          <Col xs={24} lg={12}>
            <Form.Item
              label={t("auth.signUp.participantFullNameEnField")}
              name="fullNameEn"
              rules={[
                {
                  required: true,
                  message: t("auth.signUp.requiredFieldMessage"),
                },
                {
                  pattern: /^[A-Za-z\s'-]+$/,
                  message: t("auth.signUp.onlyEnglishLettersMessage"),
                },
              ]}
            >
              <Input maxLength={50} showCount className="english-field" />
            </Form.Item>
          </Col>

          <Col xs={24} lg={12}>
            <Form.Item
              label={t("auth.signUp.participantFullNameArField")}
              name="fullNameAr"
              rules={[
                {
                  required: true,
                  message: t("auth.signUp.requiredFieldMessage"),
                },
                {
                  pattern: /^[\u0600-\u06FF\u0750-\u077F\s'-]+$/,
                  message: t("auth.signUp.onlyArabicLettersMessage"),
                },
              ]}
            >
              <Input maxLength={50} showCount className="arabic-field" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[24, 0]}>
          <Col xs={24} lg={12}>
            <Form.Item
              label={t("auth.signUp.civilIdNumberField")}
              name="civilIdNumber"
              rules={[
                {
                  required: true,
                  message: t("auth.signUp.requiredFieldMessage"),
                },
                {
                  pattern: /^(?:\d*)$/,
                  message: t("auth.signUp.civilIdFormatMessage"),
                },
                {
                  min: 12,
                  max: 12,
                  message: t("auth.signUp.civilIdLengthMessage"),
                },
              ]}
            >
              <Input className="english-field" />
            </Form.Item>
          </Col>

          <Col xs={24} lg={12}>
            <Form.Item
              label={t("auth.signUp.dateOfBirthField")}
              name="dateOfBirth"
              rules={[
                {
                  required: true,
                  message: t("auth.signUp.requiredFieldMessage"),
                },
              ]}
            >
              <DatePicker
                format="YYYY-MM-DD"
                style={{ width: "100%" }}
                className="english-field"
                onChange={(dateObj, dateString) => {
                  // dateString will be in the format "YYYY-MM-DD"
                  setDateOfBirth(dateString);
                }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[24, 0]}>
          <Col xs={24} lg={12}>
            <Form.Item
              label={t("auth.signUp.mobileNumberField")}
              name="mobileNumber"
              help={t("auth.signUp.whatsAppCommunication")}
              rules={[
                {
                  required: true,
                  message: t("auth.signUp.requiredFieldMessage"),
                },
              ]}
            >
              <PhoneInput
                className="english-field"
                country="kw"
                placeholder=""
                excludeCountries={["il"]}
                preferredCountries={["kw"]}
                onChange={(value, data) => {
                  const countryCode = data.dialCode;
                  const index = value.indexOf(countryCode);
                  const length = countryCode.length;
                  setMobileNumber(countryCode + "-" + value.slice(index + length));
                }}
              />
            </Form.Item>
          </Col>

          <Col xs={24} lg={12}>
            <Form.Item
              label={t("auth.signUp.genderField")}
              name="gender"
              rules={[
                {
                  required: true,
                  message: t("auth.signUp.requiredFieldMessage"),
                },
              ]}
            >
              <Select>
                <Option key="" value="">
                  -----
                </Option>
                ,
                <Option key="Male" value="Male">
                  {t("auth.signUp.maleOption")}
                </Option>
                ,
                <Option key="Female" value="Female">
                  {t("auth.signUp.femaleOption")}
                </Option>
                ,
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[24, 0]}>
          <Col xs={24} lg={12}>
            <Form.Item
              label={t("auth.signUp.guardianMobileNumberField")}
              name="guardianMobileNumber"
              rules={[
                {
                  required: true,
                  message: t("auth.signUp.requiredFieldMessage"),
                },
              ]}
            >
              <PhoneInput
                className="english-field"
                country="kw"
                placeholder=""
                excludeCountries={["il"]}
                preferredCountries={["kw"]}
                onChange={(value, data) => {
                  const countryCode = data.dialCode;
                  const index = value.indexOf(countryCode);
                  const length = countryCode.length;
                  setGuardianMobileNumber(countryCode + "-" + value.slice(index + length));
                }}
              />
            </Form.Item>
          </Col>

          <Col xs={24} lg={12}>
            <Form.Item
              label={t("auth.signUp.guardianRelationField")}
              name="guardianRelation"
              rules={[
                {
                  required: true,
                  message: t("auth.signUp.requiredFieldMessage"),
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[24, 0]}>
          <Col xs={24} lg={12}>
            <Form.Item
              label={t("auth.signUp.emergencyContactNumberField")}
              name="emergencyContactNumber"
              rules={[
                {
                  required: true,
                  message: t("auth.signUp.requiredFieldMessage"),
                },
              ]}
            >
              <PhoneInput
                className="english-field"
                country="kw"
                placeholder=""
                excludeCountries={["il"]}
                preferredCountries={["kw"]}
                onChange={(value, data) => {
                  const countryCode = data.dialCode;
                  const index = value.indexOf(countryCode);
                  const length = countryCode.length;
                  setEmergencyContactNumber(countryCode + "-" + value.slice(index + length));
                }}
              />
            </Form.Item>
          </Col>

          <Col xs={24} lg={12}>
            <Form.Item
              label={t("auth.signUp.emergencyContactRelationField")}
              name="emergencyContactRelation"
              rules={[
                {
                  required: true,
                  message: t("auth.signUp.requiredFieldMessage"),
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[24, 0]}>
          <Col xs={24} lg={14}>
            <Form.Item
              label={t("auth.signUp.languagesField")}
              name="languages"
              rules={[
                {
                  required: true,
                  message: t("auth.signUp.requiredFieldMessage"),
                },
              ]}
            >
              <Checkbox.Group
                style={{ width: "100%" }}
                onChange={(checkedValues) => {
                  setLanguages(checkedValues);
                }}
              >
                <Row className="mb-1 mx-0">
                  <Col>
                    <Checkbox value="Arabic">{t("auth.signUp.arabicOption")}</Checkbox>
                  </Col>
                </Row>

                <Row className="mb-1 mx-0">
                  <Col>
                    <Checkbox value="English">{t("auth.signUp.englishOption")}</Checkbox>
                  </Col>
                </Row>

                <Row className="mb-1 mx-0">
                  <Col>
                    <Checkbox value="Others">{t("auth.signUp.othersOption")}</Checkbox>
                  </Col>
                </Row>
              </Checkbox.Group>
            </Form.Item>
          </Col>

          {languages.includes("Others") && (
            <Col xs={24} lg={10}>
              <Form.Item
                label={t("auth.signUp.otherLanguagesField")}
                name="otherLanguages"
                rules={[
                  {
                    required: true,
                    message: t("auth.signUp.requiredFieldMessage"),
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          )}
        </Row>
        <Row gutter={[24, 0]}>
          <Col xs={24} lg={12}>
            <Form.Item
              label={t("auth.signUp.chronicDiseasesYesNoField")}
              name="chronicDiseasesYesNo"
              rules={[
                {
                  required: true,
                  message: t("auth.signUp.requiredFieldMessage"),
                },
              ]}
            >
              <Select onChange={(value) => setChronicDiseasesYesNoField(value)}>
                <Option key="" value="">
                  -----
                </Option>
                ,
                <Option key="Yes" value="Yes">
                  {t("auth.signUp.yesOption")}
                </Option>
                ,
                <Option key="No" value="No">
                  {t("auth.signUp.noOption")}
                </Option>
                ,
              </Select>
            </Form.Item>
          </Col>

          {chronicDiseasesYesNo === "Yes" && (
            <Col xs={24} lg={12}>
              <Form.Item
                label={t("auth.signUp.chronicDiseasesField")}
                name="chronicDiseases"
                rules={[
                  {
                    required: true,
                    message: t("auth.signUp.requiredFieldMessage"),
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          )}
        </Row>
        <Row gutter={[24, 0]}>
          <Col xs={24} lg={12}>
            <Form.Item
              label={t("auth.signUp.nationalityField")}
              name="nationality"
              rules={[
                {
                  required: true,
                  message: t("auth.signUp.requiredFieldMessage"),
                },
              ]}
            >
              <Select showSearch className="english-field">
                <Option key="" value="">
                  -----
                </Option>
                ,{countriesList},
                <Option key="Undefined" value="Undefined">
                  Undefined (غير محددي الجنسية)
                </Option>
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} lg={12}>
            <Form.Item
              label={t("auth.signUp.motherNationalityField")}
              name="motherNationality"
              rules={[
                {
                  required: true,
                  message: t("auth.signUp.requiredFieldMessage"),
                },
              ]}
            >
              <Select showSearch className="english-field">
                <Option key="" value="">
                  -----
                </Option>
                ,{countriesList},
                <Option key="Undefined" value="Undefined">
                  Undefined (غير محددي الجنسية)
                </Option>
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} lg={16}>
            <Form.Item
              label={t("auth.signUp.countryOfResidenceField")}
              name="countryOfResidence"
              rules={[
                {
                  required: true,
                  message: t("auth.signUp.requiredFieldMessage"),
                },
              ]}
            >
              <Select showSearch className="english-field">
                <Option key="" value="">
                  -----
                </Option>
                ,{countriesList}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={[24, 0]}>
          <Col xs={24}>
            <Form.Item className="text-center">
              <Button
                style={{ width: "60%" }}
                type="primary"
                htmlType="submit"
                loading={submitting}
                disabled={submitting}
              >
                {t("auth.participantProfile.saveBtn")}
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Card>
  );
};

export default ParticipantInfoCard;
